import { DataClassName } from '@/Data/const'

/**
 * Curated header synonyms per entity, keyed by the CRM field the value is
 * written to (the backend attribute key). Each array lists the informal names
 * people actually type in CSV headers — abbreviations (`fname`), colloquialisms
 * (German `Handy`, French `portable`, Polish `komórka`), and cross-CRM
 * conventions (`surname`, `company`) — across ALL FOUR UI languages.
 *
 * These deliberately do NOT repeat a field's official title: every LANGUAGE of
 * that title already comes from the schema at build time (see
 * `FieldCandidate.aliases`), so `Prénom`/`Vorname`/`Imię` match `givenName` for
 * free. This dictionary holds only what the schema can't carry — the slang and
 * shorthand. Accents and case don't need duplicate entries: the matcher tests a
 * normalised (accent-stripped, lowercased) tier, so `societe` also matches a
 * `Société` header.
 *
 * Entity-scoped so the same header resolves correctly per import — `nom` is a
 * surname on Person but a company name on Company. A synonym only ever fires for
 * a field that exists on the entity being imported; entries for absent fields
 * are inert. If a header matches two present fields the match is ambiguous and
 * deferred to the user (see `matchOne` in layer-fuzzy).
 */
const PERSON_SYNONYMS: Record<string, string[]> = {
  givenName: [
    // en
    'first',
    'first name',
    'firstname',
    'fname',
    'forename',
    'given',
    // de — "Rufname" is the informal spoken-name term; "Vname" abbreviation
    'rufname',
    'vname',
    // fr — "prénom" is the title; these are spreadsheet shorthands
    'prenom',
    'p-nom',
    // pl — "imię" is the title; "im." is a spreadsheet column abbreviation
    'im.',
    'im',
  ],
  familyName: [
    // en
    'last',
    'last name',
    'lastname',
    'lname',
    'surname',
    'family',
    'family name',
    'fam',
    // de — "Nachname" is a common informal synonym for "Familienname"; "NName" abbreviation
    'nachname',
    'nname',
    'familienname',
    // fr — bare "nom" is the surname in French (official title is "Nom de famille")
    'nom',
    'nom de fam',
    'nom fam',
    // pl — "nazwisko" is official; "nazw." is spreadsheet shorthand
    'nazwisko',
    'nazw.',
  ],
  email: [
    // en/universal
    'email',
    'mail',
    'e-mail',
    'e mail',
    'email address',
    // fr
    'courriel',
    'adresse mail',
    'adresse email',
    'mél',
    // de
    'e-mail-adresse',
    'emailadresse',
    // pl
    'mejl',
    'adres email',
    'adres e-mail',
  ],
  // Numbered/qualified secondary addresses. Without these a "Zweit-E-Mail" /
  // "Email 2" header is grabbed by `email` via the shared "mail" token; giving
  // email2/email3 their own normalised forms lets them win that column instead.
  email2: [
    // en
    'email 2',
    'e-mail 2',
    'second email',
    '2nd email',
    'secondary email',
    'alternate email',
    'alt email',
    // de
    'zweit-e-mail',
    'zweite e-mail',
    // fr
    'e-mail secondaire',
    'adresse e-mail 2',
    // pl
    'drugi e-mail',
  ],
  email3: [
    // en
    'email 3',
    'e-mail 3',
    'third email',
    '3rd email',
    // de
    'dritt-e-mail',
    'dritte e-mail',
    // fr
    'adresse e-mail 3',
    // pl
    'trzeci e-mail',
  ],
  phone: [
    // en
    'tel',
    'telephone',
    'phone number',
    'landline',
    'phone no',
    'tel no',
    // de — "Festnetz" = landline; "Tel.-Nr." / "Telefonnr." are column header shorthands
    'festnetz',
    'telefonnummer',
    'tel.-nr.',
    'telefonnr.',
    'tel. nr.',
    'tel nr',
    // fr — "fixe" = landline; "tél." / "n° tél" are column shorthands
    'fixe',
    'téléphone fixe',
    'telephone fixe',
    'tél.',
    'n° tél',
    'no tel',
    // pl — "telefon stacjonarny" = landline; "tel. stac." common abbreviation; "nr tel"
    'telefon stacjonarny',
    'stacjonarny',
    'tel. stac.',
    'nr tel',
    'nr tel.',
    'tel.',
  ],
  phoneMobile: [
    // en
    'mobile',
    'mobile phone',
    'cell',
    'cell phone',
    'cellphone',
    'mob',
    'gsm',
    // de — "Handy" is the standard colloquial German word for mobile phone
    'handy',
    'mobilnummer',
    'mobiltelefon',
    'mobil',
    // fr — "portable" is the standard French colloquial word; "gsm" also used
    'portable',
    'tel portable',
    'tél portable',
    'téléphone portable',
    // pl — "komórka" is the colloquial word; "tel. kom." is the common abbreviation
    'komórka',
    'komorka',
    'kom.',
    'kom',
    'tel. kom.',
    'tel. kom',
    'tel mob',
  ],
  phonePrivate: [
    // en
    'private phone',
    'home phone',
    'home',
    'personal phone',
    // de
    'privat',
    'privattelefon',
    'privatnummer',
    'privates telefon',
    // fr — "domicile" = home; "tel perso" = personal phone
    'domicile',
    'tel perso',
    'tél perso',
    'téléphone perso',
    'perso',
    // pl — "prywatny" = private; "domowy" = home; "tel. pryw."
    'prywatny',
    'domowy',
    'tel. pryw.',
    'tel pryw',
  ],
  position: [
    // en
    'job',
    'job title',
    'jobtitle',
    'title',
    'role',
    'function',
    'designation',
    // de — "Stelle" (post/position), "Tätigkeit" (occupation), "Aufgabe" (task/role)
    'funktion',
    'stelle',
    'berufsbezeichnung',
    'tätigkeitsbezeichnung',
    'tatigkeit',
    // fr — "poste" is the standard informal word; "fonction" also common
    'poste',
    'fonction',
    'titre poste',
    // pl — "stanowisko" is official; "funkcja" also used in spreadsheets
    'stanowisko',
    'funkcja',
    'stanow.',
  ],
  communicationLanguage: [
    // en
    'language',
    'lang',
    'lng',
    'locale',
    'pref language',
    'preferred language',
    // de
    'sprache',
    'kommunikationssprache',
    'bevorzugte sprache',
    // fr
    'langue',
    'langue de com',
    'langue communication',
    'langue pref',
    // pl
    'język',
    'jezyk',
    'jez.',
    'język komunikacji',
  ],
  salutation: [
    // en — NB "title" is deliberately omitted: in a contact CSV it means job
    // title far more often than Mr/Mrs, so it belongs to `position` alone.
    'gender',
    'civility',
    'form of address',
    'honorific',
    'sex',
    'sal',
    // de — "Geschlecht" = gender; "Anrede" is the title word, but "Herr/Frau" coding also used
    'geschlecht',
    'anredeform',
    'herr frau',
    // fr — "civilité" is the title word; "sexe" / "genre" also appear in exports
    'civilité',
    'civilite',
    'sexe',
    'genre',
    // pl — "płeć" = gender; "tytuł grzecznościowy" is formal
    'płeć',
    'plec',
    'tytul grzecznosciowy',
    'p.',
    'pan/pani',
  ],
  group: [
    // en
    'segment',
    'list',
    'audience',
    'category',
    'tag',
    'grp',
    // de — "Gruppe" is the title; "Liste" / "Segment" are common imports
    'liste',
    'kontaktgruppe',
    // fr
    'groupe',
    'liste',
    'audience',
    // pl
    'lista',
    'segment',
    'lista kontaktów',
  ],
  status: [
    // en
    'state',
    'stat',
    // de
    'zustand',
    'kontaktstatus',
    // fr
    'état',
    'etat',
    'statut',
    // pl
    'stan',
    'status kontaktu',
  ],
  description: [
    // en
    'comment',
    'comments',
    'note',
    'notes',
    'remark',
    'remarks',
    'memo',
    'annotation',
    'desc',
    // de
    'kommentar',
    'anmerkung',
    'notiz',
    'notizen',
    'hinweis',
    'bemerkung',
    'beschreibung',
    // fr
    'commentaire',
    'remarque',
    'remarques',
    'note interne',
    'observations',
    // pl
    'komentarz',
    'uwagi',
    'notatka',
    'notatki',
    'opis',
  ],
  accountManager: [
    // en
    'owner',
    'responsible',
    'sales rep',
    'salesrep',
    'am',
    'ae',
    'account manager',
    'assigned to',
    'rep',
    // de — "Betreuer" = caretaker/account manager; "Vertriebsmitarbeiter" = sales staff
    'betreuer',
    'vertriebsmitarbeiter',
    'zuständiger',
    'zustandiger',
    'verantwortlicher',
    // fr — "commercial" = salesperson; "responsable" = account owner; "chargé de compte"
    'commercial',
    'responsable',
    'chargé de compte',
    'charge de compte',
    'resp.',
    // pl — "opiekun" = caretaker; "handlowiec" = salesperson; "odpowiedzialny"
    'opiekun',
    'handlowiec',
    'odpowiedzialny',
    'kto odpowiedzialny',
  ],
  occasion: [
    // en
    'source',
    'origin',
    'lead source',
    'how found',
    'acquisition',
    // de — "Quelle" = source; "Herkunft" = origin; "Akquisequelle" = acquisition source
    'quelle',
    'herkunft',
    'akquisequelle',
    'wie gefunden',
    // fr — "origine" = origin; "source du lead"; "provenance" = provenance
    'origine',
    'source du lead',
    'provenance',
    'comment trouvé',
    'comment trouve',
    // pl — "źródło" = source; "skąd" = where from; "pozyskanie" = acquisition
    'źródło',
    'zrodlo',
    'skąd',
    'skad',
    'pozyskanie',
    'skad klient',
  ],
}

const COMPANY_SYNONYMS: Record<string, string[]> = {
  name: [
    // en
    'company',
    'company name',
    'org',
    'organisation',
    'organization',
    'firm',
    'business',
    'account',
    'corp',
    'legal name',
    // de — "Firma" is colloquial; "Firmenname" / "Unternehmensname" are common exports
    'firma',
    'firmenname',
    'unternehmensname',
    'unternehmen',
    'betrieb',
    'gesellschaft',
    // fr — "société" is common; "raison sociale" is the legal term; "entreprise"
    'societe',
    'société',
    'raison sociale',
    'entreprise',
    'enseigne',
    'denomination',
    // pl — "firma" = firm (shared de/pl); "nazwa firmy" = company name; "przedsiębiorstwo"
    'nazwa firmy',
    'nazwa przedsiebiorstwa',
    'przedsiębiorstwo',
    'przedsiebiorstwo',
  ],
  url: [
    // en/universal
    'website',
    'web',
    'www',
    'site',
    'homepage',
    'web site',
    'url',
    'link',
    // de — "Webseite" / "Homepage" both used; "Website-URL"
    'webseite',
    'website-url',
    'internetseite',
    // fr — "site web" is standard; "adresse web"
    'site web',
    'adresse web',
    'site internet',
    // pl — "strona" = page; "strona www" / "strona internetowa"; "adres www"
    'strona',
    'strona www',
    'strona internetowa',
    'adres www',
    'adres strony',
  ],
  email: [
    // en/universal
    'email',
    'mail',
    'e-mail',
    'e mail',
    'email address',
    // fr
    'courriel',
    'adresse mail',
    'adresse email',
    'mél',
    // de
    'e-mail-adresse',
    'emailadresse',
    // pl
    'mejl',
    'adres email',
    'adres e-mail',
  ],
  // Numbered/qualified secondary addresses. Without these a "Zweit-E-Mail" /
  // "Email 2" header is grabbed by `email` via the shared "mail" token; giving
  // email2/email3 their own normalised forms lets them win that column instead.
  email2: [
    // en
    'email 2',
    'e-mail 2',
    'second email',
    '2nd email',
    'secondary email',
    'alternate email',
    'alt email',
    // de
    'zweit-e-mail',
    'zweite e-mail',
    // fr
    'e-mail secondaire',
    'adresse e-mail 2',
    // pl
    'drugi e-mail',
  ],
  email3: [
    // en
    'email 3',
    'e-mail 3',
    'third email',
    '3rd email',
    // de
    'dritt-e-mail',
    'dritte e-mail',
    // fr
    'adresse e-mail 3',
    // pl
    'trzeci e-mail',
  ],
  phone: [
    // en
    'tel',
    'telephone',
    'phone number',
    'tel no',
    'phone no',
    // de
    'festnetz',
    'telefonnummer',
    'tel.-nr.',
    'telefonnr.',
    'tel. nr.',
    // fr
    'fixe',
    'téléphone',
    'tél.',
    'n° tél',
    'no tel',
    // pl
    'nr tel',
    'nr tel.',
    'tel.',
    'telefon',
  ],
  category: [
    // en
    'cat',
    'type',
    'industry',
    'sector',
    'vertical',
    'biz type',
    // de — "Branche" = industry sector (standard term in German B2B); "Geschäftsfeld"
    'branche',
    'geschäftsfeld',
    'geschäftsbereich',
    'geschäftsart',
    'industrie',
    // fr — "branche" is shared; "secteur" = sector; "filière" = field/sector
    'secteur',
    'filière',
    'filiere',
    'branche',
    // pl — "branża" = industry/sector; "gałąź" = branch/vertical; "rodzaj działalności"
    'branża',
    'branza',
    'gałąź',
    'rodzaj działalności',
    'rodzaj dzialalnosci',
  ],
  group: [
    // en
    'segment',
    'list',
    'audience',
    'tag',
    'grp',
    // de
    'liste',
    'kontaktgruppe',
    'kundengruppe',
    // fr
    'groupe',
    'liste',
    'audience',
    // pl
    'lista',
    'segment',
    'grupa firm',
  ],
  status: [
    // en
    'state',
    'stat',
    // de
    'zustand',
    'firmenstatus',
    // fr
    'état',
    'etat',
    'statut',
    // pl
    'stan',
    'status firmy',
  ],
  statusInfo: [
    // en
    'status info',
    'status note',
    'info',
    'infos',
    'status detail',
    // de — "Statusinfo" / "Statushinweis" are typical column names
    'statusinfo',
    'statushinweis',
    'info zum status',
    // fr — "info statut" / "détail statut"
    'info statut',
    'détail statut',
    'detail statut',
    'precisions statut',
    // pl — "informacja o statusie"; "opis statusu"
    'informacja',
    'informacja o statusie',
    'opis statusu',
  ],
  description: [
    // en
    'comment',
    'comments',
    'note',
    'notes',
    'remark',
    'remarks',
    'memo',
    'annotation',
    'desc',
    // de
    'kommentar',
    'anmerkung',
    'notiz',
    'notizen',
    'hinweis',
    'bemerkung',
    'beschreibung',
    // fr
    'commentaire',
    'remarque',
    'remarques',
    'note interne',
    'observations',
    // pl
    'komentarz',
    'uwagi',
    'notatka',
    'notatki',
    'opis',
  ],
  communicationLanguage: [
    // en
    'language',
    'lang',
    'lng',
    'locale',
    'pref language',
    // de
    'sprache',
    'kommunikationssprache',
    'bevorzugte sprache',
    // fr
    'langue',
    'langue de com',
    'langue communication',
    // pl
    'język',
    'jezyk',
    'jez.',
  ],
  accountManager: [
    // en
    'owner',
    'responsible',
    'sales rep',
    'salesrep',
    'am',
    'ae',
    'account manager',
    'assigned to',
    'rep',
    // de
    'betreuer',
    'vertriebsmitarbeiter',
    'zuständiger',
    'zustandiger',
    'verantwortlicher',
    // fr
    'commercial',
    'responsable',
    'chargé de compte',
    'charge de compte',
    'resp.',
    // pl
    'opiekun',
    'handlowiec',
    'odpowiedzialny',
  ],
  occasion: [
    // en
    'source',
    'origin',
    'lead source',
    'how found',
    'acquisition',
    // de
    'quelle',
    'herkunft',
    'akquisequelle',
    // fr
    'origine',
    'source du lead',
    'provenance',
    // pl
    'źródło',
    'zrodlo',
    'skąd',
    'skad',
    'pozyskanie',
  ],
}

/** The synonym dictionary for the entity being imported. */
export function getFieldSynonyms(
  entityName: DataClassName,
): Record<string, string[]> {
  return entityName === DataClassName.COMPANY
    ? COMPANY_SYNONYMS
    : PERSON_SYNONYMS
}
